import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_OPTIONS = [
  { value: '1', label: '1st Grade' },
  { value: '2', label: '2nd Grade' },
  { value: '3', label: '3rd Grade' },
  { value: '4', label: '4th Grade' },
  { value: '5', label: '5th Grade' },
];

const SCENARIO_SUGGESTIONS = [
  // Social Situations
  'Making friends at recess',
  'Joining a group activity',
  'Starting a conversation',
  'Taking turns in games',
  
  // Sensory & Regulation (ASD-specific)
  'When sounds are too loud',
  'Feeling overwhelmed in crowded places',
  'Needing a break when overstimulated',
  'Handling unexpected touch',
  
  // Transitions & Changes (ASD-specific)
  'Dealing with changes in routine',
  'Transitioning between activities',
  'When plans change unexpectedly',
  'Going to a new place',
  
  // Emotional Regulation (ASD-specific)
  'Managing big feelings',
  'Calming down when upset',
  'Understanding others\' feelings',
  'Expressing emotions appropriately',
  
  // Communication (ASD-specific)
  'Asking for help',
  'Understanding body language',
  'Waiting for my turn to speak',
  'Using words instead of actions',
  
  // Daily Living
  'Getting ready for school',
  'Trying new foods',
  'Going to the doctor',
  'Handling disappointment',
];

const StoryCreator = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [studentInfo, setStudentInfo] = useState({
    name: '',
    age: '',
    grade: '',
    appearance: '',
    interests: '',
    strengths: '',
  });
  
  const [scenario, setScenario] = useState('');
  const [situationDetails, setSituationDetails] = useState('');

  const handleStudentInfoChange = (field, value) => {
    setStudentInfo(prev => ({ ...prev, [field]: value }));
  };

  const isStep1Valid = () => {
    return studentInfo.name && studentInfo.age && studentInfo.grade && studentInfo.appearance;
  };

  const isStep2Valid = () => {
    return scenario && situationDetails;
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    
    try {
      const response = await axios.post(`${API}/stories/generate`, {
        student_info: {
          ...studentInfo,
          age: parseInt(studentInfo.age)
        },
        scenario,
        situation_details: situationDetails
      }, { withCredentials: true });
      
      toast.success('Story created successfully!');
      navigate(`/story/${response.data.story.story_id}`);
    } catch (error) {
      console.error('Error generating story:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate story');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderProgressBar = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-semibold transition-all ${
              step >= s 
                ? 'bg-primary text-white' 
                : 'bg-secondary text-text-muted'
            }`}
          >
            {step > s ? <Check className="w-5 h-5" /> : s}
          </div>
          {s < 3 && (
            <div className={`w-12 sm:w-20 h-1 mx-1 rounded-full transition-all ${
              step > s ? 'bg-primary' : 'bg-secondary'
            }`}></div>
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="animate-fade-in-up" data-testid="step-1-form">
      <h2 className="font-heading font-bold text-2xl text-text-primary mb-2 text-center">
        Tell us about the student
      </h2>
      <p className="font-body text-text-secondary text-center mb-8">
        This helps us create a personalized story with relatable illustrations.
      </p>
      
      <div className="grid gap-6 max-w-xl mx-auto">
        <div>
          <Label className="font-body font-semibold text-text-secondary mb-2 block">
            What is the student's name?
          </Label>
          <Input
            value={studentInfo.name}
            onChange={(e) => handleStudentInfoChange('name', e.target.value)}
            placeholder="e.g., Alex"
            className="border-2 border-primary/20 rounded-xl px-4 py-3 focus:border-primary"
            data-testid="student-name-input"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="font-body font-semibold text-text-secondary mb-2 block">
              Age
            </Label>
            <Input
              type="number"
              min="5"
              max="12"
              value={studentInfo.age}
              onChange={(e) => handleStudentInfoChange('age', e.target.value)}
              placeholder="e.g., 7"
              className="border-2 border-primary/20 rounded-xl px-4 py-3 focus:border-primary"
              data-testid="student-age-input"
            />
          </div>
          <div>
            <Label className="font-body font-semibold text-text-secondary mb-2 block">
              Grade
            </Label>
            <Select 
              value={studentInfo.grade} 
              onValueChange={(value) => handleStudentInfoChange('grade', value)}
            >
              <SelectTrigger className="border-2 border-primary/20 rounded-xl px-4 py-3" data-testid="student-grade-select">
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent>
                {GRADE_OPTIONS.map((grade) => (
                  <SelectItem key={grade.value} value={grade.value}>
                    {grade.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div>
          <Label className="font-body font-semibold text-text-secondary mb-2 block">
            What does the student look like?
          </Label>
          <Textarea
            value={studentInfo.appearance}
            onChange={(e) => handleStudentInfoChange('appearance', e.target.value)}
            placeholder="e.g., A boy with curly brown hair, brown eyes, and a friendly smile. He wears glasses and loves his blue backpack."
            className="border-2 border-primary/20 rounded-xl px-4 py-3 focus:border-primary min-h-[100px]"
            data-testid="student-appearance-input"
          />
          <p className="font-body text-xs text-text-muted mt-1">
            This helps create images that look like the student
          </p>
        </div>
        
        <div>
          <Label className="font-body font-semibold text-text-secondary mb-2 block">
            What are their interests? (optional)
          </Label>
          <Input
            value={studentInfo.interests}
            onChange={(e) => handleStudentInfoChange('interests', e.target.value)}
            placeholder="e.g., dinosaurs, drawing, soccer"
            className="border-2 border-primary/20 rounded-xl px-4 py-3 focus:border-primary"
            data-testid="student-interests-input"
          />
        </div>
        
        <div>
          <Label className="font-body font-semibold text-text-secondary mb-2 block">
            What are their strengths? (optional)
          </Label>
          <Input
            value={studentInfo.strengths}
            onChange={(e) => handleStudentInfoChange('strengths', e.target.value)}
            placeholder="e.g., kind to others, creative, good listener"
            className="border-2 border-primary/20 rounded-xl px-4 py-3 focus:border-primary"
            data-testid="student-strengths-input"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="animate-fade-in-up" data-testid="step-2-form">
      <h2 className="font-heading font-bold text-2xl text-text-primary mb-2 text-center">
        What situation should the story address?
      </h2>
      <p className="font-body text-text-secondary text-center mb-8">
        Describe the social situation or challenge the student is facing.
      </p>
      
      <div className="grid gap-6 max-w-xl mx-auto">
        <div>
          <Label className="font-body font-semibold text-text-secondary mb-2 block">
            Scenario or situation
          </Label>
          <Input
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            placeholder="e.g., Making friends at recess"
            className="border-2 border-primary/20 rounded-xl px-4 py-3 focus:border-primary"
            data-testid="scenario-input"
          />
          
          {/* Scenario Categories */}
          <div className="mt-4 space-y-3">
            <div>
              <p className="font-body text-xs text-text-muted mb-2">Social Situations</p>
              <div className="flex flex-wrap gap-2">
                {SCENARIO_SUGGESTIONS.slice(0, 4).map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setScenario(suggestion)}
                    className={`px-3 py-1 rounded-full text-xs font-body transition-all ${
                      scenario === suggestion
                        ? 'bg-primary text-white'
                        : 'bg-secondary/50 text-text-secondary hover:bg-secondary'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <p className="font-body text-xs text-text-muted mb-2">Sensory & Regulation (ASD)</p>
              <div className="flex flex-wrap gap-2">
                {SCENARIO_SUGGESTIONS.slice(4, 8).map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setScenario(suggestion)}
                    className={`px-3 py-1 rounded-full text-xs font-body transition-all ${
                      scenario === suggestion
                        ? 'bg-primary text-white'
                        : 'bg-accent/20 text-accent hover:bg-accent/30'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <p className="font-body text-xs text-text-muted mb-2">Transitions & Changes (ASD)</p>
              <div className="flex flex-wrap gap-2">
                {SCENARIO_SUGGESTIONS.slice(8, 12).map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setScenario(suggestion)}
                    className={`px-3 py-1 rounded-full text-xs font-body transition-all ${
                      scenario === suggestion
                        ? 'bg-primary text-white'
                        : 'bg-accent/20 text-accent hover:bg-accent/30'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <p className="font-body text-xs text-text-muted mb-2">Emotional Regulation (ASD)</p>
              <div className="flex flex-wrap gap-2">
                {SCENARIO_SUGGESTIONS.slice(12, 16).map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setScenario(suggestion)}
                    className={`px-3 py-1 rounded-full text-xs font-body transition-all ${
                      scenario === suggestion
                        ? 'bg-primary text-white'
                        : 'bg-accent/20 text-accent hover:bg-accent/30'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <p className="font-body text-xs text-text-muted mb-2">Communication (ASD)</p>
              <div className="flex flex-wrap gap-2">
                {SCENARIO_SUGGESTIONS.slice(16, 20).map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setScenario(suggestion)}
                    className={`px-3 py-1 rounded-full text-xs font-body transition-all ${
                      scenario === suggestion
                        ? 'bg-primary text-white'
                        : 'bg-accent/20 text-accent hover:bg-accent/30'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <p className="font-body text-xs text-text-muted mb-2">Daily Living</p>
              <div className="flex flex-wrap gap-2">
                {SCENARIO_SUGGESTIONS.slice(20).map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setScenario(suggestion)}
                    className={`px-3 py-1 rounded-full text-xs font-body transition-all ${
                      scenario === suggestion
                        ? 'bg-primary text-white'
                        : 'bg-secondary/50 text-text-secondary hover:bg-secondary'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <Label className="font-body font-semibold text-text-secondary mb-2 block">
            Tell us more about the situation
          </Label>
          <Textarea
            value={situationDetails}
            onChange={(e) => setSituationDetails(e.target.value)}
            placeholder="e.g., The student often plays alone at recess because they feel nervous approaching other children. They want to join in games but don't know how to ask."
            className="border-2 border-primary/20 rounded-xl px-4 py-3 focus:border-primary min-h-[150px]"
            data-testid="situation-details-input"
          />
          <p className="font-body text-xs text-text-muted mt-1">
            The more details you provide, the more personalized the story will be
          </p>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="animate-fade-in-up text-center" data-testid="step-3-review">
      <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
        Ready to create your story!
      </h2>
      <p className="font-body text-text-secondary mb-8">
        Review the details below and click generate.
      </p>
      
      <div className="max-w-xl mx-auto bg-white rounded-2xl p-6 shadow-card text-left mb-8">
        <div className="grid gap-4">
          <div>
            <p className="font-body text-sm text-text-muted">Student</p>
            <p className="font-heading font-semibold text-text-primary">
              {studentInfo.name}, {studentInfo.age} years old, Grade {studentInfo.grade}
            </p>
          </div>
          <div>
            <p className="font-body text-sm text-text-muted">Appearance</p>
            <p className="font-body text-text-secondary">{studentInfo.appearance}</p>
          </div>
          <div>
            <p className="font-body text-sm text-text-muted">Scenario</p>
            <p className="font-heading font-semibold text-text-primary">{scenario}</p>
          </div>
          <div>
            <p className="font-body text-sm text-text-muted">Details</p>
            <p className="font-body text-text-secondary">{situationDetails}</p>
          </div>
        </div>
      </div>
      
      {isGenerating ? (
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <div className="text-center">
            <p className="font-heading font-semibold text-text-primary text-lg gentle-pulse">
              Writing your story...
            </p>
            <p className="font-body text-text-secondary text-sm mt-1">
              This may take a moment
            </p>
          </div>
        </div>
      ) : (
        <Button
          onClick={handleGenerate}
          className="bg-accent text-white hover:bg-accent/90 rounded-full px-10 py-4 text-lg font-body font-semibold shadow-soft transition-all active:scale-95"
          data-testid="generate-story-btn"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Generate Story
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="story-creator-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-text-secondary hover:text-primary"
            data-testid="back-to-dashboard-btn"
          >
            Back to Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-12 max-w-4xl mx-auto">
        {renderProgressBar()}
        
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        
        {/* Navigation Buttons */}
        <div className="flex justify-between mt-12 max-w-xl mx-auto">
          {step > 1 && !isGenerating ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full px-6 py-2"
              data-testid="prev-step-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <div></div>
          )}
          
          {step < 3 && (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 ? !isStep1Valid() : !isStep2Valid()}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-2 ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="next-step-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default StoryCreator;
